import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import * as THREE from 'three'
import { ArrowHelper } from 'three';

import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { ThreeDModel } from '../lib/definitions';

import {Button, Checkbox, Modal, Progress, Space, Text} from '@mantine/core'

import styles from './ThreeDViewer.module.scss';
import { consoleError } from '../lib/logger';
import { useFullscreen } from '@mantine/hooks';

import { useDisclosure, useElementSize, useMergedRef } from '@mantine/hooks';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faCompressAlt, faExpandWide, faQuestionCircle } from '@fortawesome/sharp-solid-svg-icons';

import * as Sentry from "@sentry/nextjs";
import Link from 'next/link';

type location =
{
    x: number,
    y: number
}

export type ThreeDViewerHandle = {
	showOptions: () => void;
	showHelp: () => void;
	fullScreen:() => void;
  };

// eslint-disable-next-line react/display-name
export const ThreeDViewer = forwardRef<ThreeDViewerHandle, {model: ThreeDModel}>((props, ref) => {

	useImperativeHandle(ref, () => ({
		showOptions()
		{
			openOptions();
		},
		showHelp()
		{
			openHelp();
		},
		fullScreen()
		{
			toggleFullScreen();
		}
	
}));
	
	//const fullScreenHandle = useFullScreenHandle();
	
	const [loading, setLoading] = useState(false);
	const loaded = useRef(false);
	const [percentageLoaded, setPercentageLoaded] = useState(0);
	const [percentageText, setPercentageText] = useState("");
	//const [fullScreen, setFullScreen] = useState(false);
	const {ref: fullScrenRef, toggle: toggleFullScreen, fullscreen } = useFullscreen();


	const canvas3d = useRef<HTMLCanvasElement|null>(null);
	const canvas2d = useRef<HTMLCanvasElement|null>(null);

    const scene = useRef(new THREE.Scene());
    const pickingScene = useRef(new THREE.Scene());
    const pickingTarget = useRef(new THREE.WebGLRenderTarget());
    const renderer = useRef<THREE.WebGLRenderer|null>(null);
    const camera = useRef(new THREE.PerspectiveCamera());
    const pivotPoint =useRef(new THREE.Object3D());
    const arrow =useRef(new THREE.ArrowHelper());
    const controls =useRef<OrbitControls|null>(null);

    const originalCursor = useRef<location>({x:0, y:0});
    const lastCursor = useRef<location>({x:0, y:0});

	const [openedHelp, { open:openHelp, close:closeHelp }] = useDisclosure(false);
	const [openedOptions, { open:openOptions, close: closeOptions }] = useDisclosure(false);
	
    const mouseDragging = useRef(false);
    const touchDragging = useRef(false);
    const dragging = useRef(false);

	const light = useRef(new THREE.DirectionalLight(0xffffff, 0.9));
	const light2 = useRef(new THREE.DirectionalLight(0x5555ff, 0.1));
	const ambientLight = useRef(new THREE.AmbientLight(0xffffff, 0.1));

	const { ref:resizeRef, width, height } = useElementSize();

	const mergedRef = useMergedRef(resizeRef, canvas3d);

	const labelRadius = 0.005;

	const whiteColour = new THREE.Color("white");

	const [errored, setErrored] = useState(false);
	const [permissionDenied, setPermissionDenied] = useState(false);

	const scale = useRef(1);
	const boxSize = useRef(new THREE.Vector3());
	type Options = 
	{
		colouredBackground : boolean,
		showArrow: boolean,
		showReflectedLight: boolean,
		showAmbientLight: boolean,
		showLabels : boolean,
		showColours : boolean,
		showTexture: boolean
	}

	const [options, setOptions] = useState<Options>({colouredBackground: true, 
		showArrow:true,
		 showAmbientLight: true,
		 showReflectedLight:true,
		 showColours: true,
		 showLabels: true,
		showTexture: true
		});

	type Note =
	{
		note : string,
		object: THREE.Mesh,
		offset: number,
		coordinates: THREE.Vector3
	}

	const notes = useRef<Note[]>([]);


///	let sphereGroup = useRef(new THREE.Group());


	const rotationScale = 100;
	const raycaster = useRef(new THREE.Raycaster());
	const mouse = useRef(new THREE.Vector2());
	const rgbaBuffer = useRef(new Uint8Array(4));

	const v4 = useRef(new THREE.Vector4());

	const unpackDownscale = 255 / 256;
	const unpackFactors = new THREE.Vector4(
		unpackDownscale / (256 * 256 * 256),
		unpackDownscale / (256 * 256),
		unpackDownscale / 256,
		unpackDownscale
	);

	const material = useRef<THREE.MeshStandardMaterial | null>(null);

	const currentModel = useRef<ThreeDModel>({});

	useEffect(() =>
	{

		if (!props.model.accessible || !props.model.filename)
		{
			setPermissionDenied(true);
			return;
		}
		Sentry.addBreadcrumb({message:"Loading 3d model for " + props.model.name});
		setLoading(true);
		
		initializeViewer(props.model).then(() =>
			{

			Sentry.addBreadcrumb({message:"Loaded 3d model for " + props.model.name});
			resizeRendererIfNecessary();
		updateMaterial();
		updateOptions();
		requestAnimationFrame(animate);
	}).catch((e)=>
	{
		setLoading(false);
		setErrored(true);
		Sentry.captureException(e);
	});

	return (() =>
	{
	scene.current.clear();
	}
	)

	},[]);

	useEffect(() =>
	{
		updateOptions();
		requestAnimationFrame(animate);
	}, [options]);

	useEffect(()=>
	{
		resizeRendererIfNecessary();
	
	},[width,height]);

	function resizeRendererIfNecessary()
	{
		if (canvas3d.current)
		{

		resizeRendererToDisplaySize();
		camera.current.aspect = canvas3d.current.clientWidth / canvas3d.current.clientHeight;
		camera.current.updateProjectionMatrix();
		requestAnimationFrame(animate);
	}

	}

	function updateMaterial() {

	if (!currentModel.current?.hasTexture) {
		return;
    }

	//console.log(material.current);

	scene.current?.traverse(object =>
	{
		
		switch (object.type) {
			case "Mesh":
			//case "Object3D":
				if (!object.name.startsWith("label_") && !(object instanceof THREE.Light)) {
					if (object.parent?.type != "ArrowHelper") {
						//console.log("Updating material of " + object.name);
					
						let o = object as any;
						o.material = (options.showTexture ? o.originalMaterial : material.current);
					}
				}

				break;
		}
	})
}

function updateOptions() {

	if (!material.current || !arrow.current)
	{
		return;
	}
	scene.current.background = options.colouredBackground ? (new THREE.Color(0x050533)) : (new THREE.Color(0));
	light2.current.visible = options.showReflectedLight;
	arrow.current.visible = options.showArrow;
	ambientLight.current.visible = options.showAmbientLight;
	light.current.intensity = options.showAmbientLight ? 0.9 : 1;

	material.current.needsUpdate = true;
}

function setTexture(t: THREE.Texture) {
	t.wrapS = t.wrapT = THREE.RepeatWrapping;
	t.offset.set(0, 0);
	t.repeat.set(4, 4);
	t.encoding = THREE.sRGBEncoding;
	t.flipY = false;
}

async function initializeViewer(model:ThreeDModel)
{
	//console.log(canvas2d.current);
	//console.log(canvas3d.current);

	Sentry.addBreadcrumb({message:"Initializing model for " + model.name, data:model});


	let newOptions = {... options};

	currentModel.current = model;
	newOptions.showColours = !!model.hasColours;


	if (model.hasTexture) {
		newOptions.showTexture = true;
	}

	setOptions(newOptions);
	
	if (!model.filename)
	{
		throw new Error("No filename");
	}

	const loader = new GLTFLoader();


	let gltf = await loader.loadAsync(model.filename, (e)=>
	{


		if (e.total>0)
		{
		let pc = Math.round(100 * e.loaded / e.total);
		setPercentageLoaded(pc);
		setPercentageText(pc + "%");

		}
	});


	setPercentageText("Finalizing ...");

	if (canvas3d.current)
	{

		scene.current.background = new THREE.Color(0x050533);

		//TODO: SET THE CAMERA SIZE
		camera.current = new THREE.PerspectiveCamera(30, window.innerWidth / window.innerHeight, 0.1, 1000);

		renderer.current = new THREE.WebGLRenderer({canvas:canvas3d.current, antialias:true});
		renderer.current.outputEncoding = THREE.sRGBEncoding;

		pickingScene.current.background = new THREE.Color(0x000000);

		//pickingTarget = new THREE.WebGLRenderTarget();
		pickingTarget.current.texture.minFilter = THREE.LinearFilter;

		let vs3D = `
precision mediump float;

attribute vec3 idcolor;
 varying vec3 vidcolor;

void main(){
vidcolor = idcolor;
gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0);
}`;

		let fs3D = `
precision mediump float;
varying vec3 vidcolor;

void main(void) {
gl_FragColor = vec4(vidcolor,1.0);
  }`;

		var pickingMaterial = new THREE.ShaderMaterial(
			{
				vertexShader: vs3D,
				fragmentShader: fs3D,
			});

		let arrowLocation = new THREE.Vector3(10, 10, 10);
		let arrowDirection = new THREE.Vector3().sub(arrowLocation.clone().normalize());
		arrow.current = new ArrowHelper(arrowDirection, arrowLocation, 2, 0xffff00, 1, 1);

		arrow.current.name = "rus_arrow";

		//arrow.cone.material = new THREE.MeshLambertMaterial({color: arrow.line.material.color });

		pivotPoint.current.add(arrow.current);

		controls.current = new OrbitControls(camera.current, canvas3d.current);
		controls.current.addEventListener('change',() => {requestAnimationFrame(animate)});

		camera.current.position.z = 5;
		controls.current.update();

		light.current.position.set(10, 10, 10);

		light2.current.position.copy(light.current.position.clone().multiplyScalar(-1));

		scene.current.add(ambientLight.current);

		//const axesHelper = new THREE.AxesHelper(5);
		//scene.add(axesHelper);

		setupArrow();

		pivotPoint.current.add(light.current);
		pivotPoint.current.add(light2.current);

		scene.current.add(pivotPoint.current);

		/*
		renderer.domElement.addEventListener('pointerdown', canvas_mousedown);
		renderer.domElement.addEventListener('pointerup', canvas_mouseup);
		renderer.domElement.addEventListener('pointermove', canvas_mousemove);

		renderer.domElement.addEventListener('touchstart', canvas_touchstart);
		renderer.domElement.addEventListener('touchend', canvas_touchend);
		renderer.domElement.addEventListener('touchmove', canvas_touchmove);
		*/

		if (model.rotate) {
	
			//gltf.scene.rotateY(Math.PI);
			//gltf.scene.rotateY(Math.PI);
			//gltf.scene.rotateZ(Math.PI);
        }

		
		
		let boundingBox = new THREE.Box3().setFromObject(gltf.scene);
		boundingBox.getSize(boxSize.current);

		scale.current = 1.4 / boxSize.current.length();

		gltf.scene.scale.set(scale.current, scale.current, scale.current);
		boundingBox.getSize(boxSize.current);
		boundingBox = new THREE.Box3().setFromObject(gltf.scene);
		boundingBox.getSize(boxSize.current);

		let centre = new THREE.Vector3();
		boundingBox.getCenter(centre);
		gltf.scene.translateX(-centre.x);
		gltf.scene.translateY(-centre.y);
		gltf.scene.translateZ(-centre.z)

		gltf.scene.updateMatrixWorld(true);

		if (model.replaceMaterial || currentModel.current.hasTexture) {
			let textureLoader = new THREE.TextureLoader();


			let bumpMap = textureLoader.load("/rus-3d/maps/PlasterNaturalPaint001_BUMP_1K.jpg");
			let displacementMap = textureLoader.load("/rus-3d/maps/PlasterNaturalPaint001_DISP_1K.jpg");
			let roughnessMap = textureLoader.load("/rus-3d/maps/PlasterNaturalPaint001_ROUGH_1K.jpg");
			//let normalMap = textureLoader.load("/rus-3d/maps/PlasterNaturalPaint001_NRM_1K.png");
			
			setTexture(bumpMap);
			setTexture(displacementMap);
			setTexture(roughnessMap);
			//setTexture(normalMap);

			material.current = new THREE.MeshStandardMaterial(
				{
					color: 0xffffff,
					bumpMap: bumpMap,
					bumpScale: 0.0005,
					displacementMap: displacementMap,
					displacementScale: 0.001 / scale.current, //I think that since that deforms the mesh it needs to be scaled too
					roughnessMap: roughnessMap,
					roughness: 1
					//normalMap: normalMap,
					//normalScale: new THREE.Vector2(1, 1)				

				});

			if (newOptions.showColours) {
				material.current.vertexColors = true;
            }
			}

		}

		notes.current = [];

		let re = /(.+)[ ._]+?[0-9][0-9][1-9]$/;

		gltf.scene.traverse(object => {			

			switch (object.type) {
				case "Mesh":
				case "Object3D":
					let mesh = null;
					let index = 0xffffff;
					if (object.name.startsWith("label_")) {
						object.visible = false;
						let note = object.name.substr(6);

						note = note.replace(/_/g, ' ');

						let k = note.match(re);

						if (k) {
							note = k[1];
                        }

						index = notes?.current.length + 1;
						const g2 = new THREE.SphereGeometry(labelRadius, 32, 32);
						mesh = new THREE.Mesh(g2, pickingMaterial);
						let bb = new THREE.Box3().setFromObject(object);
						bb.getCenter(mesh.position);
					

						notes.current.push({ note: note, object: mesh, offset: Math.random(), coordinates: new THREE.Vector3(0,0,0) });
					
					}
					else {
						let o = object as any;

						if (model.replaceMaterial) {
							o.material = material.current;
						}

						if (model.replaceColour && o.material) {
							o.material.color = whiteColour;
                        }
						if (currentModel.current.hasTexture) {
							o.originalMaterial = o.material;
                        }

						if (object.type == "Mesh") {
							mesh = new THREE.Mesh(o.geometry, pickingMaterial);
							mesh.scale.set(scale.current, scale.current, scale.current);
							object.getWorldPosition(mesh.position);
						}

					}

					if (!(mesh == null)) {

						if (currentModel.current.hasLabels) {
							pickingScene.current.add(mesh);
						}

						let positions = mesh.geometry.attributes["position"].array;
						let idColor = new Float32Array(positions.length);
						let color = new THREE.Color();
						color.setHex(index);

						for (let j = 0; j < positions.length; j += 3) {
							idColor[j] = color.r;
							idColor[j + 1] = color.g;
							idColor[j + 2] = color.b;
						}

						mesh.geometry.setAttribute('idcolor', new THREE.BufferAttribute(idColor, 3));
					}
					break;
				case "Group":
					break;
				default:
					object.visible = false;
			}
		});

		scene.current.add(gltf.scene);

		//see https://stackoverflow.com/questions/15331358/three-js-get-object-size-with-respect-to-camera-and-object-position-on-screen/15331885#15331885
		let vFOV = camera.current.fov * Math.PI / 180;

		let h = boxSize.current.y;
		let v = boxSize.current.length() * 0.7;

		let dist = (h / 2) / Math.tan(vFOV / 2) + boxSize.current.z / 2;

		camera.current.position.z = dist
		arrow.current.position.copy(light.current.position.clone().normalize().multiplyScalar(v));
		var arrowLength = boxSize.current.length() / 10;
		arrow.current.setLength(arrowLength, arrowLength/2, arrowLength/2);
		setupArrow();
		//animate();

		setPercentageLoaded(100);
		setPercentageText("100%");
		setLoading(false);
		loaded.current= true;

	}



function setupArrow() {
let arrowDirection = (new THREE.Vector3()).sub(arrow.current.position).normalize();
	arrow.current.setDirection(arrowDirection);

}


function startDragging(x: number, y: number) {

	if (controls.current)
	{
		controls.current.enabled=false;
	}
	dragging.current = true;
	originalCursor.current.x = x;
	originalCursor.current.y = y;

	lastCursor.current.x = originalCursor.current.x;
	lastCursor.current.y = originalCursor.current.y;
}

function dragLight(x:number, y: number) {
	if (dragging.current) {
		
let deltaX = x - lastCursor.current.x;
		let deltaY = y - lastCursor.current.y;

		//console.log("delta:" + deltaX + "," + deltaY);

		let v = new THREE.Vector3();

		const direction = camera.current.getWorldDirection(v);
		let vector = new THREE.Vector3(0, 1, 1).unproject(camera.current);
		vector.cross(direction);
		vector.normalize();

		let v2 = new THREE.Vector3(1, 0, 1).unproject(camera.current);
		v2.cross(direction);
		v2.normalize();

		pivotPoint.current.rotateOnWorldAxis(v2, deltaX / rotationScale)
		pivotPoint.current.rotateOnWorldAxis(vector, -deltaY / rotationScale)

		setupArrow();

		lastCursor.current.x = x;
		lastCursor.current.y = y;

		requestAnimationFrame(animate);
	}
}

function stopDragging() {
if (!controls.current)
{
	return;
}

	controls.current.enabled = true;
	controls.current.update();
	dragging.current = false;
	touchDragging.current = false;
	mouseDragging.current = false;

}
function canvas_touchstart(e: React.TouchEvent) {
	if (e.touches.length == 3) {
startDragging(e.touches[0].clientX, e.touches[0].clientY);
		touchDragging.current = true;
		e.preventDefault();
		e.stopPropagation();
	}
}

function canvas_touchend(e : React.TouchEvent) {
stopDragging();
}

function canvas_touchmove(e: React.TouchEvent) {
	if (touchDragging.current) {

//console.log(e.touches[0].clientX + "," + e.touches[0].clientY);
dragLight(e.touches[0].clientX, e.touches[0].clientY);
		e.stopPropagation();
		e.preventDefault();
	}

}

function canvas_mousedown(e : React.MouseEvent) {

	if (e.ctrlKey) {
		e.preventDefault();
		e.stopPropagation();
		startDragging(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
		mouseDragging.current = true;

	}
}

function canvas_mouseup(e : React.MouseEvent) {
stopDragging();
}

function canvas_mousemove(e: React.MouseEvent) {

	if (mouseDragging.current) {
dragLight(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
		e.stopPropagation();
		e.preventDefault();
	}
}

/*
function resizeCanvas2d() {
	
	if (!canvas2d.current)
	{
		return;
	}
	const pixelRatio = window.devicePixelRatio;

	const width = canvas2d.current.clientWidth * pixelRatio;
	const height = canvas2d.current.clientHeight * pixelRatio;

	// If it's resolution does not match change it
	if (canvas2d.current.width !== width || canvas2d.current.height !== height) {
		canvas2d.current.width = width;
		canvas2d.current.height = height;
		return true;
	}

	return false;
}*/


function resizeRendererToDisplaySize() {

	if (!renderer.current || !canvas2d.current)
	{
		return false;
	}
	const canvas = renderer.current.domElement;
	const pixelRatio = window.devicePixelRatio;
	const width = canvas.clientWidth * pixelRatio | 0;
	const height = canvas.clientHeight * pixelRatio | 0;

	canvas2d.current.width = width;
	canvas2d.current.height = height;




		renderer.current.setSize(width, height, false);
		pickingTarget.current.setSize(width, height, 0);

}

function getCanvasCoordinates(v : THREE.Vector3) : THREE.Vector3 {

	if (!canvas3d.current)
	{
		return new THREE.Vector3(0,0,0);
	}

	let o = new THREE.Vector3();
	o.copy(v);

	const pixelRatio = window.devicePixelRatio;
	var width = canvas3d.current.clientWidth * pixelRatio, height = canvas3d.current.clientHeight * pixelRatio;
	var widthHalf = width / 2, heightHalf = height / 2;
	o.project(camera.current);

	o.x = (o.x * widthHalf) + widthHalf;
	o.y = - (o.y * heightHalf) + heightHalf;
	return o;
}

function animate() {
	if (!canvas2d.current || !renderer.current || !canvas3d.current)
	{
		return;
	}

	if (!loaded.current)
	{
		return;
	}

	//TODO - need to separate out the setting of the display size to the animation

	/*
	if (resizeRendererToDisplaySize()) {
		camera.current.aspect = canvas3d.current.clientWidth / canvas3d.current.clientHeight;
		camera.current.updateProjectionMatrix();
	}
	*/

	scene.current.updateMatrixWorld(true);

	//sphereGroup.visible = false;

	renderer.current.render(scene.current, camera.current);
	//renderer.render(pickingScene, camera);

	//resizeCanvas2d();

	let ctx = canvas2d.current.getContext("2d");

	if (!ctx)
	{
		return;
	}


	ctx.fillStyle = "#FF0000";

	let frustum = new THREE.Frustum();
	frustum.setFromProjectionMatrix(new THREE.Matrix4().multiplyMatrices(camera.current.projectionMatrix, camera.current.matrixWorldInverse));
	ctx.clearRect(0, 0, canvas2d.current.width, canvas2d.current.height);
	//ctx.fillRect(20, 20, 500, 600);

	ctx.fillStyle = "#FF0000";
	ctx.strokeStyle = "#6666ff";
	ctx.lineWidth = 2;

	let v = new THREE.Vector3();

	//sphereGroup.current.visible = true;

	//renderer.setClearColor(0xffffff, 1);
	//renderer.clear();


	let visibleNotes = [];

	if (options.showLabels) {
		renderer.current.setRenderTarget(pickingTarget.current);
		renderer.current.render(pickingScene.current, camera.current);
		renderer.current.setRenderTarget(null);

		for (let i = 0; i < notes.current.length; i++) {
			let note = notes.current[i];
			let object = note.object;
			object.getWorldPosition(v);

			//object.localToWorld(v);

			if (frustum.containsPoint(v)) {

				//project to normalized device space
				let o = getCanvasCoordinates(v);

				//find out what's at that space

				try {
					let pixelBuffer = new Uint8Array(4);
					renderer.current.readRenderTargetPixels(pickingTarget.current, o.x, pickingTarget.current.height - o.y, 1, 1, pixelBuffer);
					let id = (pixelBuffer[0] << 16) | (pixelBuffer[1] << 8) | (pixelBuffer[2]);
					if (id > 0 && id < 0xffffff) {
						let n2 = notes.current[id - 1];

						if (n2 == note) {
							//console.log(id);
							note.coordinates = o;
							visibleNotes.push(note);
						}
					}

				}
				catch (e) {
					consoleError(e);
				}

			}

		}

		visibleNotes.sort((a, b) => (a.coordinates.z > b.coordinates.z) ? -1 : ((b.coordinates.z > a.coordinates.z) ? 1 : 0));


		ctx.lineWidth = 2;
		const pixelRatio = window.devicePixelRatio;
		const textHeight = 16;

		ctx.font = textHeight * pixelRatio + "px Arial";

		for (let i = 0; i < visibleNotes.length; i++) {
			let note = visibleNotes[i];
			let coords = note.coordinates;

			ctx.lineWidth = 2;
			ctx.beginPath();
			ctx.fillStyle = "#FF0000";
			ctx.strokeStyle = "#6666ff";

			ctx.arc(coords.x, coords.y, 5, 0, 2 * Math.PI);
			ctx.fill();

			ctx.lineWidth = 1;
			ctx.strokeStyle = "#ff0000";
			ctx.moveTo(coords.x, coords.y);

			let offset = 20 + note.offset * 30;
			let toX = coords.x + offset * pixelRatio;
			let toY = coords.y - offset * pixelRatio

			ctx.lineTo(toX, toY);
			ctx.stroke();

			ctx.fillStyle = "#FF0000";
			ctx.strokeStyle = "#6666ff";
			ctx.lineWidth = 2;
			const padding = 5;
			let textSize = ctx.measureText(note.note);

			ctx.beginPath();
			ctx.fillStyle = "#FFFFFF";
			ctx.strokeStyle = "#ff0000";

			ctx.rect(toX, toY, (textSize.width + padding * 2 * pixelRatio)  , -(textHeight + padding * 2) * pixelRatio );

			ctx.fill();
			ctx.stroke();
			ctx.fillStyle = "#000000";
			ctx.fillText(note.note, toX + padding * pixelRatio, toY - (padding +2) * pixelRatio);

		}
	}

	//requestAnimationFrame(animate);
}

if (errored)
{
	return <p>There was a problem loading the 3d model. Please try <a href="#" onClick={()=>{window.location.reload();}}>refreshing the page</a>.</p>
}

return (
	<>
	<div ref={fullScrenRef}>
	<div className={styles.canvasContainer}>
	<canvas key='3d' ref={mergedRef}  className={styles.canvas3d}
	onPointerDown={canvas_mousedown}
	onPointerUp={canvas_mouseup}
	onPointerMove={canvas_mousemove}
	onTouchStart={canvas_touchstart}
	onTouchEnd={canvas_touchend}
	onTouchMove={canvas_touchmove}
	/>
	<canvas key='2d' ref={canvas2d} className={styles.canvas2d}/>
	{!loaded.current && <img className={styles.poster} src={props.model.previewFilename}/>}
	{permissionDenied && <div className={styles.noAccess}>To access this 3d model, please <Link href='/subscription'>purchase a Raw Umber Gold subscription</Link></div>}
	{loading && <div className={styles.loading}>
		<Text>Loading ...</Text>
		<Progress value={percentageLoaded} label={percentageText} size='xl' mt='lg'/>
		</div>}

	{loaded.current && <div className={styles.controlBar}>
		<FontAwesomeIcon icon={fullscreen?faCompressAlt:faExpandWide} onClick={toggleFullScreen}/>	
		{!fullscreen && <FontAwesomeIcon icon={faQuestionCircle} onClick={openHelp}/>}
		{!fullscreen && <FontAwesomeIcon icon={faCog} onClick={openOptions}/>}

	</div>

	}

	</div>
	</div>	

	<Modal opened={openedHelp} onClose={closeHelp} withCloseButton={false}>
	<Text>Click and drag the screen to rotate the model, or use your fingers. Use your mouse wheel / scroll to zoom in and out. Hold the shift key down and click and drag to pan.</Text>
<Space h='md'/>
<Text>If you&rsquo;re using a table or phone you can use your fingers to scroll and zoom.</Text>
<Space h='md'/>
<Text>To move the light, hold the ctrl/cmd key down while you click and drag, or use three fingers and drag on your phone / tablet.</Text>

<Button mt='lg' onClick={closeHelp}>Close</Button>
	</Modal>

	<Modal opened={openedOptions} onClose={closeOptions}  withCloseButton={false} overlayProps={{opacity: 0.4}}>
	<Checkbox mb='sm' label='Use a coloured background' checked={options.colouredBackground} onChange={(event) => setOptions({...options, colouredBackground: event.currentTarget.checked})}/>
	<Checkbox mb='sm' label='Show where the light is coming from' checked={options.showArrow} onChange={(event) => setOptions({...options, showArrow: event.currentTarget.checked})}/>
	<Checkbox mb='sm' label='Show reflected light' checked={options.showReflectedLight} onChange={(event) => setOptions({...options, showReflectedLight: event.currentTarget.checked})}/>
	<Checkbox mb='sm' label='Show ambient light' checked={options.showAmbientLight} onChange={(event) => setOptions({...options, showAmbientLight: event.currentTarget.checked})}/>
	{!!props.model.hasColours && <Checkbox mb='sm' label='Show colours' checked={options.showColours} onChange={(event) => setOptions({...options, showColours: event.currentTarget.checked})}/>}
	{!!props.model.hasLabels && <Checkbox mb='sm' label='Show labels' checked={options.showLabels} onChange={(event) => setOptions({...options, showLabels: event.currentTarget.checked})}/>}

	<Button mt='lg' onClick={closeOptions}>Close</Button>

	</Modal>

	</>

);
}
);



